import { useState } from 'react';
import { Pause, Play, Archive } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { analytics } from '../lib/analytics';

interface BreakModeSettings {
  isActive: boolean;
  startDate?: string;
  reason?: string;
  pausedData?: any;
}

export function RelationshipBreakMode() {
  const [breakMode, setBreakMode] = useState<BreakModeSettings>(() => {
    const saved = localStorage.getItem('tension_break_mode');
    return saved ? JSON.parse(saved) : { isActive: false };
  });
  const [reason, setReason] = useState('');

  const activateBreakMode = () => {
    const settings: BreakModeSettings = {
      isActive: true,
      startDate: new Date().toISOString(),
      reason: reason.trim() || undefined,
    };

    localStorage.setItem('tension_break_mode', JSON.stringify(settings));
    setBreakMode(settings);
    
    analytics.track('break_mode_activated', {
      hasReason: !!reason.trim(),
    });

    toast.success('Break mode activated. Your data is paused but preserved.');
  };

  const deactivateBreakMode = () => {
    const endDate = new Date().toISOString();
    const duration = breakMode.startDate 
      ? Math.floor((new Date(endDate).getTime() - new Date(breakMode.startDate).getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    localStorage.removeItem('tension_break_mode');
    setBreakMode({ isActive: false });
    
    analytics.track('break_mode_deactivated', {
      durationDays: duration,
    });

    toast.success('Welcome back! Break mode deactivated.');
  };

  const archiveRelationship = () => {
    const archived = JSON.parse(localStorage.getItem('tension_archived_relationships') || '[]');
    const currentData = {
      archivedDate: new Date().toISOString(),
      checkins: localStorage.getItem('tension_checkins'),
      personalization: localStorage.getItem('tension_personalization'),
      triggers: localStorage.getItem('tension_triggers'),
      breakModeReason: breakMode.reason,
      breakModeDuration: breakMode.startDate 
        ? Math.floor((Date.now() - new Date(breakMode.startDate).getTime()) / (1000 * 60 * 60 * 24))
        : 0,
    };

    archived.push(currentData);
    localStorage.setItem('tension_archived_relationships', JSON.stringify(archived));

    // Clear current relationship data but keep account/subscription
    const keysToKeep = [
      'tension_access_token',
      'tension_user',
      'tension_subscription',
      'tension_purchased_packs',
      'tension_age_verified',
      'tension_milestones',
      'tension_reminder_settings',
      'tension_cloud_sync_enabled',
      'tension_auto_sync',
      'tension_device_id',
      'tension_last_sync_time',
    ];

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('tension_') && !keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    analytics.track('relationship_archived', {
      hadBreakMode: breakMode.isActive,
    });

    toast.success('Relationship archived. You can start fresh whenever you\'re ready.');
    window.location.reload();
  };

  if (!breakMode.isActive) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium flex items-center gap-2 mb-2">
              <Pause className="w-5 h-5" />
              Break Mode
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Taking a break from your relationship? Pause tracking without losing your data.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="breakReason">
              Reason (optional, for your records)
            </Label>
            <Textarea
              id="breakReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Taking time apart to reflect, temporary separation, personal growth period..."
              rows={3}
              aria-label="Enter reason for break mode"
            />
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Break mode preserves all your data:</strong>
              <br />
              Your check-ins, patterns, and insights stay safe. You can resume tracking anytime.
            </p>
          </div>

          <Button onClick={activateBreakMode} variant="outline" className="w-full">
            <Pause className="w-4 h-4 mr-2" />
            Activate Break Mode
          </Button>
        </div>
      </Card>
    );
  }

  const daysSinceBreak = Math.floor(
    (Date.now() - new Date(breakMode.startDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Card className="p-6 border-amber-200 dark:border-amber-800">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2 mb-2 text-amber-900 dark:text-amber-100">
            <Pause className="w-5 h-5" />
            Break Mode Active
          </h3>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Tracking paused for {daysSinceBreak} {daysSinceBreak === 1 ? 'day' : 'days'}
          </p>
        </div>

        {breakMode.reason && (
          <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Your note:</strong> {breakMode.reason}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={deactivateBreakMode} variant="outline" className="flex-1">
            <Play className="w-4 h-4 mr-2" />
            Resume Tracking
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="flex-1">
                <Archive className="w-4 h-4 mr-2" />
                End Relationship
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive This Relationship?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-3">
                  <p>
                    This will archive all data from this relationship and clear your current tracking.
                    You'll be able to start fresh with a new relationship.
                  </p>
                  <p className="text-amber-600 dark:text-amber-400">
                    <strong>Your archived data will be preserved</strong> and can be exported later
                    if needed.
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={archiveRelationship}>
                  Archive & Start Fresh
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <p className="text-xs text-neutral-500 dark:text-neutral-500 text-center">
          Your data is safe and preserved while in break mode
        </p>
      </div>
    </Card>
  );
}
